import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { 
  User, 
  Mail, 
  Lock, 
  Save,
  Eye,
  EyeOff,
  Shield,
  Crown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { profileAPI } from '../services/api';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  // Get user role
  const userRole = typeof user?.role === 'object' ? user?.role?.name : user?.role;
  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  // Determine the correct profile API based on user role
  const getProfileEndpoint = () => {
    switch (userRole) {
      case 'SUPER_ADMIN':
        return profileAPI.getSuperAdminProfile();
      case 'ADMIN':
        return profileAPI.getAdminProfile();
      case 'MANAGER':
        return profileAPI.getManagerProfile();
      case 'EMPLOYEE':
      default:
        return profileAPI.getProfile();
    }
  };

  const updateProfileEndpoint = (profileData) => {
    switch (userRole) {
      case 'SUPER_ADMIN':
        return profileAPI.updateSuperAdminProfile(profileData);
      case 'ADMIN':
        return profileAPI.updateAdminProfile(profileData);
      case 'MANAGER':
        return profileAPI.updateManagerProfile(profileData);
      case 'EMPLOYEE':
      default:
        return profileAPI.updateProfile(profileData);
    }
  };

  const { data: profile, isLoading } = useQuery(
    ['user-profile', userRole], // Include role in query key for proper caching
    getProfileEndpoint,
    {
      select: (response) => response.data,
      enabled: !!user, // Only fetch when user is available
    }
  );

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      ...(isSuperAdmin && { email: '' }),
    }
  });
  
  // Update form when profile data is loaded
  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        ...(isSuperAdmin && { email: profile.email || '' }),
      });
    }
  }, [profile, reset, isSuperAdmin]);

  const updateMutation = useMutation(
    (data) => updateProfileEndpoint(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-profile', userRole]);
        toast.success('Profile updated successfully');
        setIsEditing(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update profile');
      },
    }
  );

  const onSubmit = async (data) => {
    // For super admin, include email; for others, exclude it
    if (!isSuperAdmin) {
      const { email, ...profileData } = data;
      await updateMutation.mutateAsync(profileData);
    } else {
      await updateMutation.mutateAsync(data);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'ADMIN':
        return <Shield className="w-5 h-5 text-blue-500" />;
      case 'EMPLOYEE':
        return <User className="w-5 h-5 text-green-500" />;
      default:
        return <User className="w-5 h-5 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-yellow-100 text-yellow-800';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800';
      case 'EMPLOYEE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account information</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-1"
        >
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center">
              <div className="mx-auto h-24 w-24 rounded-full bg-primary-500 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-white">
                  {profile?.firstName?.charAt(0)}{profile?.lastName?.charAt(0)}
                </span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {profile?.firstName} {profile?.lastName}
              </h2>
              <p className="text-gray-500 mb-2">@{profile?.username}</p>
              <div className="flex items-center justify-center space-x-2 mb-4">
                {getRoleIcon(typeof profile?.role === 'object' ? profile?.role?.name : profile?.role)}
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(typeof profile?.role === 'object' ? profile?.role?.name : profile?.role)}`}>
                  {typeof profile?.role === 'object' ? profile?.role?.name : profile?.role}
                </span>
              </div>
              {profile?.createdAt && (
                <p className="text-sm text-gray-500">
                  Member since {new Date(profile?.createdAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Profile Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">Personal Information</h2>
              {!isEditing && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsEditing(true)}
                  className="btn-primary"
                >
                  Edit Profile
                </motion.button>
              )}
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">
                    <User className="w-4 h-4 inline mr-2" />
                    First Name
                  </label>
                  <input
                    {...register('firstName', { required: 'First name is required' })}
                    type="text"
                    className="form-input"
                    disabled={!isEditing}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    <User className="w-4 h-4 inline mr-2" />
                    Last Name
                  </label>
                  <input
                    {...register('lastName', { required: 'Last name is required' })}
                    type="text"
                    className="form-input"
                    disabled={!isEditing}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address
                </label>
                {isSuperAdmin ? (
                  <>
                    <input
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      type="email"
                      className="form-input"
                      disabled={!isEditing}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type="email"
                      value={profile?.email || ''}
                      className="form-input bg-gray-50"
                      disabled
                    />
                    <p className="mt-1 text-sm text-gray-500">Email cannot be changed</p>
                  </>
                )}
              </div>

              <div>
                <label className="form-label">
                  <User className="w-4 h-4 inline mr-2" />
                  Username
                </label>
                <input
                  type="text"
                  value={profile?.username}
                  className="form-input bg-gray-50"
                  disabled
                />
                <p className="mt-1 text-sm text-gray-500">Username cannot be changed</p>
              </div>

              {isEditing && (
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      reset();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    disabled={updateMutation.isLoading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {updateMutation.isLoading ? (
                      <div className="spinner"></div>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </motion.button>
                </div>
              )}
            </form>
          </div>
        </motion.div>
      </div>

      {/* Account Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">Account Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">User ID</label>
            <input
              type="text"
              value={profile?.id}
              className="form-input bg-gray-50"
              disabled
            />
          </div>
          <div>
            <label className="form-label">Account Status</label>
            <input
              type="text"
              value={profile?.isActive ? 'Active' : 'Inactive'}
              className="form-input bg-gray-50"
              disabled
            />
          </div>
          <div>
            <label className="form-label">Created At</label>
            <input
              type="text"
              value={new Date(profile?.createdAt).toLocaleString()}
              className="form-input bg-gray-50"
              disabled
            />
          </div>
          <div>
            <label className="form-label">Last Updated</label>
            <input
              type="text"
              value={new Date(profile?.updatedAt).toLocaleString()}
              className="form-input bg-gray-50"
              disabled
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Profile;

